"use client";

import { motion } from "framer-motion";
import { Network, Database, Layers, Workflow } from "lucide-react";

export default function TechnologyPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-20"
      >
        <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
          深度神经网络架构
        </h1>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          探索 旺旺助手2.0 背后的黑科技。我们将强化学习、CFR 算法与大规模集群算力完美结合。
        </p>
      </motion.div>

      {/* Architecture Diagram Concept */}
      <div className="relative mb-32 p-8 glass-panel rounded-xl overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(0,243,255,0.1)_0%,transparent_70%)]"></div>
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="p-6 border border-white/10 bg-black/50 rounded-lg relative group">
            <div className="absolute inset-0 bg-neon-cyan/5 group-hover:bg-neon-cyan/10 transition-colors"></div>
            <Database className="w-12 h-12 text-neon-cyan mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">数据采集层</h3>
            <p className="text-sm text-gray-400">实时解析数千万手历史牌局，构建多维度的对手特征库。</p>
          </div>
          
          <div className="p-6 border border-white/10 bg-black/50 rounded-lg relative group">
            <div className="absolute inset-0 bg-neon-magenta/5 group-hover:bg-neon-magenta/10 transition-colors"></div>
            <Network className="w-12 h-12 text-neon-magenta mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">深度推理层</h3>
            <p className="text-sm text-gray-400">基于 Deep CFR 算法的实时纳什均衡计算，预测胜率分布。</p>
          </div>
          
          <div className="p-6 border border-white/10 bg-black/50 rounded-lg relative group">
            <div className="absolute inset-0 bg-neon-blue/5 group-hover:bg-neon-blue/10 transition-colors"></div>
            <Workflow className="w-12 h-12 text-neon-blue mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">决策执行层</h3>
            <p className="text-sm text-gray-400">结合对手漏洞进行剥削调整，输出最佳操作（Bet, Call, Fold）。</p>
          </div>
        </div>
        
        {/* Connecting Lines (Decorative) */}
        <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-blue z-0 opacity-50"></div>
      </div>

      {/* Details Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-24">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <Layers className="w-16 h-16 text-neon-cyan mb-6" />
          <h2 className="text-3xl font-bold mb-4">GTO 与剥削的完美平衡</h2>
          <p className="text-gray-400 leading-relaxed mb-6">
            传统的求解器（Solver）只能提供静态的纳什均衡（GTO）策略。但在实战中，面对非完美的真实玩家，单纯打 GTO 无法实现利益最大化。
          </p>
          <p className="text-gray-400 leading-relaxed">
            我们的模型引入了实时对手建模系统（Opponent Modeling）。当发现对手具有极高的 Fold to 3-Bet 率或较低的 WTSD 时，AI 会自动偏离 GTO 节点，采取高侵略性的剥削策略。
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative h-80 rounded-xl overflow-hidden glass-panel border border-neon-cyan/30 flex items-center justify-center"
        >
          {/* Mock Visualization */}
          <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
          <div className="text-center z-10">
            <div className="text-neon-cyan font-mono text-5xl font-bold mb-2">99.8%</div>
            <div className="text-gray-400 tracking-widest text-sm">GTO 近似逼近率</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
